import { useQueryClient } from '@zg-rentals/react-query5';
import { DatarouterError } from '@zg-rentals/rental-manager-env';
import {
  useRentalManagerUserPropertyMultiplexWebApiHandler_create,
  useRentalManagerUserPropertyMultiplexWebApiHandler_getUnitNumbers,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import type {
  UserPropertyMultiplexCreateUnitRequestWebDto,
  UserPropertyMultiplexCreateUnitResponseWebDto,
  UserPropertyMultiplexCreateUnitsResponseWebDto,
} from '@zg-rentals/ts-rental-manager-api-client';

export type CreateMultiplexResult = {
  multiplexBuildingSessionId: string;
  allUnitsFailed: boolean;
  hasPartialUnitsSucceeded: boolean;
  successfulUnits: Array<UserPropertyMultiplexCreateUnitResponseWebDto>;
  failedUnits: Array<UserPropertyMultiplexCreateUnitResponseWebDto>;
};

type UseMultiplexUnitCreationParams = {
  fullAddress: string;
  customerId?: string;
};

export const useMultiplexUnitCreation = ({ fullAddress, customerId }: UseMultiplexUnitCreationParams) => {
  const queryClient = useQueryClient();

  const {
    data: unitNumbersResponse,
    error: unitNumbersError,
    isError: isErrorUnitNumbers,
    isLoading: isLoadingUnitNumbers,
  } = useRentalManagerUserPropertyMultiplexWebApiHandler_getUnitNumbers({ fullAddress });

  const existingUnitNumbers = unitNumbersResponse?.unitNumbers ?? [];

  const { isPending, mutate: createMultiplexMutation } = useRentalManagerUserPropertyMultiplexWebApiHandler_create({});

  const createMultiplex = (
    units: Array<UserPropertyMultiplexCreateUnitRequestWebDto>,
    callbacks: {
      onSuccess: (result: CreateMultiplexResult) => void;
      onError: (errorMessage: string) => void;
    },
  ) => {
    createMultiplexMutation(
      {
        postData: {
          ...(customerId && { customerId }),
          fullAddress,
          units,
        },
      },
      {
        onSuccess: (response: UserPropertyMultiplexCreateUnitsResponseWebDto) => {
          void queryClient.invalidateQueries({ queryKey: ['getProperties'], refetchType: 'all' });

          const { multiplexBuildingSessionId, units: responseUnits } =
            response ?? ({} as UserPropertyMultiplexCreateUnitsResponseWebDto);

          const failedUnits = responseUnits.filter((unit) => !unit.success);
          const successfulUnits = responseUnits.filter((unit) => unit.success);
          const allUnitsFailed = responseUnits.every((unit) => !unit.success);
          const hasPartialUnitsSucceeded = successfulUnits.length > 0 && failedUnits.length > 0;

          if (allUnitsFailed) {
            const errorMessages = failedUnits.map((unit) => `Unit ${unit.unitNumber}: ${unit.errorMessage}`).join(' ');
            callbacks.onError(errorMessages);
            return;
          }

          callbacks.onSuccess({
            multiplexBuildingSessionId,
            allUnitsFailed,
            hasPartialUnitsSucceeded,
            successfulUnits,
            failedUnits,
          });
        },
        onError: (error) => {
          callbacks.onError(DatarouterError.getDisplayMessage(error));
        },
      },
    );
  };

  return {
    existingUnitNumbers,
    isLoadingUnitNumbers,
    isErrorUnitNumbers,
    unitNumbersError,
    isPending,
    createMultiplex,
  };
};
